import React, { useEffect, useRef, useState } from 'react';
import { Button, Icon, Tabs } from '@innovationnorway/cl-react';
import { MODULE_TYPES } from 'src/features/authorization/hooks/use-auth';
import {
  getFFOverride,
  setFFOverride,
  useFFOverrideRevision,
} from 'src/features/authorization/hooks/use-local-ff-overrides';
import './FeatureFlagDevPanel.scss';

const FeatureFlagDevPanel: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  useFFOverrideRevision(); // re-render when any override changes

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!popupRef.current?.contains(target) && !toggleRef.current?.contains(target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const featureFlags = MODULE_TYPES.filter((m) => m.startsWith('FF_'));
  const modules = MODULE_TYPES.filter((m) => !m.startsWith('FF_'));

  const renderList = (items: (typeof MODULE_TYPES)[number][]) => (
    <div className="ff-panel__list">
      {items.map((flag) => {
        const override = getFFOverride(flag);
        return (
          <div key={flag} className="ff-panel__row">
            <span className="ff-panel__label">{flag}</span>
            <div className="ff-panel__actions">
              {([true, false, null] as const).map((val) => {
                const label = val === null ? 'server' : val ? 'on' : 'off';
                const isActive = override === val;
                return (
                  <button
                    key={label}
                    type="button"
                    aria-pressed={isActive}
                    className={`ff-panel__action${isActive ? ' ff-panel__action--active' : ''}`}
                    onClick={() => setFFOverride(flag, val)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="ff-panel">
      <Button
        ref={toggleRef}
        icon
        variant="tertiary"
        className="ff-panel__toggle"
        aria-label="Feature flags"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-pressed={isOpen}
      >
        <Icon prefix="inno" iconName="Flag" />
      </Button>

      {isOpen && (
        <div className="ff-panel__popup" ref={popupRef}>
          <Tabs defaultValue="ff">
            <Tabs.List>
              <Tabs.Tab value="ff">Feature flags</Tabs.Tab>
              <Tabs.Tab value="modules">Modules</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="ff">{renderList(featureFlags)}</Tabs.Panel>
            <Tabs.Panel value="modules">{renderList(modules)}</Tabs.Panel>
          </Tabs>
        </div>
      )}
    </div>
  );
};

export default FeatureFlagDevPanel;
